import Autocomplete from '@betterfunnel/ui-core/Autocomplete';
import ColorPickerField from '@betterfunnel/ui-core/ColorPickerField';
import TextArea from '@betterfunnel/ui-core/TextArea';
import TextField from '@betterfunnel/ui-core/TextField';
import Toggle from '@betterfunnel/ui-core/Toggle';
import { toCSS, toJSON } from 'css-convert-json';
import { useCallback, useContext, useEffect, useState } from 'react';
import FileUploadInput from '../../../components/FormFields/FileUpload/FileUploadInput';
import SettingSectionTitle from '../../../components/settings/SettingSectionTitle';
import { DashboardContext } from '../../../contexts/DashboardContext';
import { SnackbarContext } from '../../../contexts/SnackbarContext';
import { UserContext } from '../../../contexts/UserContext';
import { updateDynamicPage } from '../../../services/DynamicPagesService';
import { isValidCss } from '../../../utils/validator';
import { ALLOWED_FONTS } from '../../Brand';

export const ManageDynamicPageSettingsDesign = () => {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage, accountBrand } = useContext(DashboardContext);

  const [buttonCSS, setButtonCSS] = useState<string>('');

  useEffect(() => {
    const brand = selectedDynamicPage?.data().brand;
    if (!brand?.buttonCSS) return;
    if (buttonCSS) return;

    setButtonCSS(toCSS(brand?.buttonCSS as any).trim());
  }, [selectedDynamicPage, buttonCSS]);

  const updateDynamicPageData = useCallback(
    async (data: any) => {
      if (!selectedAccount || !selectedDynamicPage) return;
      try {
        await updateDynamicPage(
          selectedAccount.id,
          selectedDynamicPage.id,
          data,
        );
        show('Updated');
      } catch (_) {
        show('Failed to update');
      }
    },
    [selectedAccount, selectedDynamicPage, show],
  );

  return (
    <div>
      <SettingSectionTitle title="Logo" description="Add your logo." />

      <div className="mb-6">
        <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
          <div className="pt-6 sm:flex">
            <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
              Logo
            </dt>
            <div className="w-full max-w-[415px]">
              <FileUploadInput
                image={
                  selectedDynamicPage?.data().brand?.logo ?? accountBrand?.logo
                }
                onSelected={(image) => {
                  updateDynamicPageData({
                    brand: {
                      ...selectedDynamicPage?.data().brand,
                      ...(image ? { logo: image } : {}),
                    },
                  });
                }}
              />
            </div>
          </div>
        </dl>
      </div>

      <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none">
        <div>
          <h2 className="text-base leading-7 font-semibold text-gray-900">
            Color Pallet
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom button styling to your landing pages.
          </p>

          <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
            <div className="pt-6 pb-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Primary Color
              </dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <ColorPickerField
                  value={
                    selectedDynamicPage?.data().brand?.primaryColor ??
                    accountBrand?.primaryColor
                  }
                  debounce
                  onChange={(v) =>
                    updateDynamicPageData({
                      brand: {
                        ...selectedDynamicPage?.data().brand,
                        ...(v ? { primaryColor: v } : {}),
                      },
                    })
                  }
                />
              </dd>
            </div>
            <div className="pt-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Secondary Color
              </dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <ColorPickerField
                  value={
                    selectedDynamicPage?.data().brand?.secondaryColor ??
                    accountBrand?.secondaryColor
                  }
                  debounce
                  onChange={(v) =>
                    updateDynamicPageData({
                      brand: {
                        ...selectedDynamicPage?.data().brand,
                        ...(v ? { secondaryColor: v } : {}),
                      },
                    })
                  }
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base leading-7 font-semibold text-gray-900">
            Buttons
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom button styling to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
            <div className="pt-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Custom Button CSS
              </dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <TextArea
                  value={buttonCSS}
                  validation={{
                    status: !isValidCss(buttonCSS) ? 'error' : undefined,
                    text: !isValidCss(buttonCSS) ? 'Invalid CSS' : undefined,
                  }}
                  onChange={(e) => {
                    const newButtonCSS = e.target.value;
                    setButtonCSS(newButtonCSS);

                    if (isValidCss(newButtonCSS)) {
                      console.log(
                        'updated!!!',
                        newButtonCSS,
                        toJSON(newButtonCSS),
                      );
                      updateDynamicPageData({
                        brand: {
                          ...selectedDynamicPage?.data().brand,
                          ...(newButtonCSS
                            ? { buttonCSS: toJSON(newButtonCSS) }
                            : {}),
                        },
                      });
                    }
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base leading-7 font-semibold text-gray-900">
            Font
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom font to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
            <div className="pt-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Font
              </dt>
              <dd className="mt-1 flex items-center justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <Autocomplete
                  selected={
                    selectedDynamicPage?.data().brand?.font ??
                    accountBrand?.font ??
                    'Inter'
                  }
                  options={ALLOWED_FONTS}
                  onChange={(selected) => {
                    updateDynamicPageData({
                      brand: {
                        ...selectedDynamicPage?.data().brand,
                        ...(selected ? { font: selected } : {}),
                      },
                    });
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base leading-7 font-semibold text-gray-900">
            Border Radius
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom border radius to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
            <div className="pt-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Border radius
              </dt>
              <dd className="mt-1 flex items-center gap-x-6 sm:mt-0 sm:flex-auto">
                <TextField
                  className="h-16 max-w-[64px] text-center"
                  style={{
                    borderRadius:
                      selectedDynamicPage?.data().brand?.borderRadius ??
                      accountBrand?.borderRadius ??
                      6,
                  }}
                  value={`${selectedDynamicPage?.data().brand?.borderRadius ?? accountBrand?.borderRadius ?? 6}`}
                  onChange={(e) => {
                    const newVal = e.target.value;

                    try {
                      const num = parseFloat(newVal || '0');
                      updateDynamicPageData({
                        brand: {
                          ...selectedDynamicPage?.data().brand,
                          borderRadius: num,
                        },
                      });
                    } catch (_) {
                      /* empty */
                    }
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base leading-7 font-semibold text-gray-900">
            Shadow
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom shadow to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
            <div className="pt-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Shadow
              </dt>
              <dd className="mt-1 flex items-center justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <Toggle
                  checked={
                    selectedDynamicPage?.data().brand?.shadow ??
                    accountBrand?.shadow ??
                    true
                  }
                  onChange={(checked) => {
                    updateDynamicPageData({
                      brand: {
                        ...selectedDynamicPage?.data().brand,
                        shadow: checked,
                      },
                    });
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};
